/**
 * Kaufbereich der Produktseite — Baumpaten Deutschland
 * Polar Marketing, Oktober 2026
 *
 * Eine Stelle, die alles synchron hält: Gesamtpreis, Stückpreis, Paketkarten, Mengenfeld,
 * Versandhinweis, die mitlaufende Leiste und die versteckte Waldflächen-Angabe.
 *
 * Vorher lagen dafür drei konkurrierende Skripte im Theme, die sich über Timer gegenseitig
 * überschrieben, ungescoped auf die ganze Seite zugriffen und beim Variantenwechsel nicht
 * nachrechneten. Das hier ersetzt sie vollständig.
 *
 * Alles ist auf das <bp-product>-Element beschränkt, damit Quick-Buy-Schubladen und
 * Produktkarten auf derselben Seite nicht dazwischenfunken.
 */
(() => {
  'use strict';

  if (window.customElements.get('bp-product')) return;

  /* ------------------------------------------------------------------ Geldbeträge formatieren
   * Gleiche Logik wie im Theme (assets/theme.js), damit Trennzeichen und Währungszeichen
   * überall identisch aussehen — inklusive Tausenderpunkt, an dem die alte Lösung scheiterte.
   */
  const moneyFormat = () =>
    (window.themeVariables &&
      window.themeVariables.settings &&
      (window.themeVariables.settings.currencyCodeEnabled
        ? window.themeVariables.settings.moneyWithCurrencyFormat
        : window.themeVariables.settings.moneyFormat)) ||
    '{{amount_with_comma_separator}} €';

  function withDelimiters(cents, precision, thousands, decimal) {
    const number = (Number(cents) / 100).toFixed(precision);
    const parts = number.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    const format = moneyFormat();
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder || isNaN(cents)) return '';

    let value;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function readJson(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[bp-product] Daten konnten nicht gelesen werden:', error);
      return null;
    }
  }

  class BpProduct extends HTMLElement {
    connectedCallback() {
      this.variants = readJson(this.querySelector('[data-bp-variants]')) || [];
      this.waldflaechen = readJson(this.querySelector('[data-bp-waldflaeche-map]')) || {};

      this.unitSingular = this.getAttribute('unit-singular') || '';
      this.unitPlural = this.getAttribute('unit-plural') || this.unitSingular;

      this.quantityInput = this.querySelector('input[name="quantity"]');
      this.currentVariant = this.variants[0] || null;

      const form = this.querySelector('form[id]');
      const selected = form && form.querySelector('[name="id"]');
      if (selected) this.setVariantById(selected.value);

      this.addEventListener('variant:change', this.onVariantChange);
      this.addEventListener('click', this.onClick);

      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', this.onQuantityChange);
        this.quantityInput.addEventListener('change', this.onQuantityChange);
      }

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('variant:change', this.onVariantChange);
      this.removeEventListener('click', this.onClick);
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('input', this.onQuantityChange);
        this.quantityInput.removeEventListener('change', this.onQuantityChange);
      }
    }

    /* ------------------------------------------------------------------------------- Zustand */
    get quantity() {
      const value = parseInt(this.quantityInput ? this.quantityInput.value : '1', 10);
      return isNaN(value) || value < 1 ? 1 : value;
    }

    setVariantById(id) {
      const match = this.variants.find((variant) => String(variant.id) === String(id));
      if (match) this.currentVariant = match;
    }

    /* ------------------------------------------------------------------------------ Ereignisse */
    onVariantChange = (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      this.setVariantById(variant.id);
      // Falls die Variante im JSON fehlt (neu angelegt, Theme noch nicht neu gerendert),
      // reichen die Werte aus dem Ereignis.
      if (!this.currentVariant || this.currentVariant.id !== variant.id) {
        this.currentVariant = {
          id: variant.id,
          price: variant.price,
          requires_shipping: variant.requires_shipping
        };
      }

      this.updateWaldflaeche(variant.id);
      this.render();
    };

    onQuantityChange = () => this.render();

    onClick = (event) => {
      const tier = event.target.closest('[data-bp-tier]');
      if (tier && this.contains(tier)) {
        this.selectQuantity(parseInt(tier.dataset.qty, 10));
        return;
      }

      // Die Plus/Minus-Knöpfe des Themes setzen den Wert, ohne ein Ereignis zu feuern.
      // Weil dieser Zuhörer am Wrapper hängt, läuft er danach und sieht den neuen Wert.
      if (event.target.closest('.quantity-selector__button')) {
        this.render();
        requestAnimationFrame(() => this.render());
      }
    };

    selectQuantity(quantity) {
      if (!this.quantityInput || isNaN(quantity) || quantity < 1) return;

      // Über den Setter des Themes, damit Mindestmenge und Feldbreite stimmen.
      if ('quantity' in this.quantityInput) {
        this.quantityInput.quantity = quantity;
      } else {
        this.quantityInput.value = String(quantity);
      }

      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    updateWaldflaeche(variantId) {
      const field = this.querySelector('[data-bp-waldflaeche]');
      if (!field) return;
      const value = this.waldflaechen[String(variantId)];
      field.value = value == null ? '' : value;
    }

    /* ---------------------------------------------------------------------------- Darstellung */
    render() {
      const variant = this.currentVariant;
      if (!variant) return;

      const quantity = this.quantity;
      const unitPrice = Number(variant.price);
      const total = unitPrice * quantity;

      this.querySelectorAll('bp-total-price').forEach((element) => {
        element.textContent = formatMoney(total);
      });

      this.querySelectorAll('bp-unit-price').forEach((element) => {
        element.textContent = formatMoney(unitPrice);
      });

      this.querySelectorAll('[data-bp-tier-price]').forEach((element) => {
        const tierQuantity = parseInt(element.dataset.qty, 10);
        if (!isNaN(tierQuantity)) element.textContent = formatMoney(unitPrice * tierQuantity);
      });

      this.querySelectorAll('[data-bp-tier]').forEach((tier) => {
        const active = parseInt(tier.dataset.qty, 10) === quantity;
        tier.classList.toggle('is-selected', active);
        tier.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const unit = quantity === 1 ? this.unitSingular : this.unitPlural;
      this.querySelectorAll('[data-bp-package]').forEach((element) => {
        element.textContent = unit ? quantity + ' ' + unit : String(quantity);
      });

      this.querySelectorAll('bp-shipping-note').forEach((element) => {
        const note = variant.requires_shipping ? element.dataset.noteShipped : element.dataset.noteDigital;
        element.textContent = note ? ' ' + note : '';
      });

      this.updateOptionSubtitles();
    }

    /**
     * Die kleine Zweitzeile der Dropdowns (bei uns die Koordinaten) wird direkt aus der
     * gewählten Option abgelesen — so bleibt die Aufteilung an einer Stelle, im Liquid.
     */
    updateOptionSubtitles() {
      this.querySelectorAll('[data-bp-option-sub]').forEach((target) => {
        const position = target.getAttribute('data-bp-option-sub');
        const selector = this.querySelector('[data-option-selector] input[name="option' + position + '"]');
        if (!selector) return;

        const list = selector.closest('[data-option-selector]');
        let option = null;
        try {
          option = list.querySelector('.popover-listbox__option[value="' + CSS.escape(selector.value) + '"]');
        } catch (error) {
          option = null;
        }

        const sub = option && option.querySelector('.bp-listbox__sub');
        target.textContent = sub ? sub.textContent.trim() : '';
        target.hidden = !sub;
      });
    }
  }

  window.customElements.define('bp-product', BpProduct);

  // Reine Anzeige-Elemente. Sie brauchen keine eigene Logik, müssen aber definiert sein,
  // damit sie sich wie Inline-Elemente verhalten und nicht erst beim Upgrade sichtbar werden.
  ['bp-total-price', 'bp-unit-price', 'bp-shipping-note'].forEach((name) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, class extends HTMLElement {});
    }
  });
})();
